import { usePageOwnerIdentityStore } from '../store/pageOwnerIdentityStore';
import type { PageOwnerIdentity } from '../dto/EntityRepository';
import {
  useAuthStore,
  useAuthTokenStore,
} from '../store/authStore';

const hasOwnerAccess = (aui: string): boolean =>
  !!useAuthTokenStore.getState().authToken &&
  useAuthStore.getState().user?.aui === aui;

const normalizePathname = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

const isCurrentArchiveRequest = (
  requestedAui: string,
  requestedOwner: PageOwnerIdentity | null,
  requestedPathname: string,
  requestedOwnerAccess: boolean,
  matchPath: boolean,
  matchOwnerAccess: boolean,
): boolean =>
  requestedOwner?.aui === requestedAui &&
  usePageOwnerIdentityStore.getState().pageOwner === requestedOwner &&
  (
    !matchOwnerAccess ||
    hasOwnerAccess(requestedAui) === requestedOwnerAccess
  ) &&
  (
    !matchPath ||
    normalizePathname(window.location.pathname) === requestedPathname
  );

export const runCurrentArchiveRequest = async <Response, Result>(
  requestedAui: string,
  request: () => Promise<Response>,
  onSuccess: (response: Response) => Result,
  options: {
    matchPath?: boolean;
    matchOwnerAccess?: boolean;
  } = {},
): Promise<Result | undefined> => {
  const requestedOwner =
    usePageOwnerIdentityStore.getState().pageOwner;
  const requestedPathname = normalizePathname(window.location.pathname);
  const requestedOwnerAccess = hasOwnerAccess(requestedAui);
  const {
    matchPath = true,
    matchOwnerAccess = true,
  } = options;

  if (
    !isCurrentArchiveRequest(
      requestedAui,
      requestedOwner,
      requestedPathname,
      requestedOwnerAccess,
      matchPath,
      matchOwnerAccess,
    )
  ) {
    return undefined;
  }

  try {
    const response = await request();
    if (
      !isCurrentArchiveRequest(
        requestedAui,
        requestedOwner,
        requestedPathname,
        requestedOwnerAccess,
        matchPath,
        matchOwnerAccess,
      )
    ) {
      return undefined;
    }

    return onSuccess(response);
  } catch (error) {
    if (
      !isCurrentArchiveRequest(
        requestedAui,
        requestedOwner,
        requestedPathname,
        requestedOwnerAccess,
        matchPath,
        matchOwnerAccess,
      )
    ) {
      return undefined;
    }

    throw error;
  }
};
